import httpService from "./httpService";

export interface PerformanceReturns {
  "1d": number | null;
  "2d": number | null;
  "3d": number | null;
  "4d": number | null;
  "5d": number | null;
  "1w": number | null;
  "2w": number | null;
  "1m": number | null;
  "3m": number | null;
  "6m": number | null;
  "1y": number | null;
  ytd: number | null;
  inception: number | null;
  all: number | null;
}

export interface PerformanceResponse {
  performance: PerformanceReturns;
  inceptionDatetimeUtc: string;
  earliestDatetimeUtc: string | null;
}

export type PerformanceProductType = "basket" | "portfolio";

export interface InvestorPerformanceBatchRequest {
  providerId: number;
  productType: PerformanceProductType;
  productId: number;
}

export interface InvestorPerformanceBatchResult extends InvestorPerformanceBatchRequest {
  response: PerformanceResponse | null;
}

export interface ProviderPerformanceBatchRequest {
  productType: PerformanceProductType;
  productId: number;
}

export interface ProviderPerformanceBatchResult extends ProviderPerformanceBatchRequest {
  response: PerformanceResponse | null;
}

const BATCH_CONCURRENCY = 10;
const MAX_REQUEST_CONCURRENCY = 20;
const CACHE_TTL_MS = 300_000; // 5 minutes
const CACHE_SWEEP_INTERVAL_MS = 120_000; // 2 minutes

interface PerformanceCacheEntry {
  response: PerformanceResponse;
  fetchedAt: number;
}

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  if (items.length === 0) return [];

  const safeConcurrency = Math.max(1, Math.min(concurrency, items.length));
  const results = new Array<R>(items.length);
  let nextIndex = 0;

  const worker = async () => {
    while (true) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      if (currentIndex >= items.length) return;
      results[currentIndex] = await mapper(items[currentIndex], currentIndex);
    }
  };

  await Promise.all(Array.from({ length: safeConcurrency }, () => worker()));
  return results;
}

function createConcurrencyLimiter(maxConcurrent: number) {
  let active = 0;
  const waiters: Array<() => void> = [];

  const flush = () => {
    if (active >= maxConcurrent) return;
    const waiter = waiters.shift();
    if (!waiter) return;
    active += 1;
    waiter();
  };

  return async function runWithLimit<T>(operation: () => Promise<T>): Promise<T> {
    await new Promise<void>((resolve) => {
      waiters.push(resolve);
      flush();
    });

    try {
      return await operation();
    } finally {
      active = Math.max(0, active - 1);
      flush();
    }
  };
}

const runWithPerformanceRequestLimit = createConcurrencyLimiter(MAX_REQUEST_CONCURRENCY);

class ProductPerformanceService {
  private cache = new Map<string, PerformanceCacheEntry>();
  private pendingRequests = new Map<string, Promise<PerformanceResponse>>();
  private lastSweepAt = 0;

  async getPerformance(
    providerId: number,
    productType: PerformanceProductType,
    productId: number
  ): Promise<PerformanceResponse> {
    this.sweepExpiredEntries();

    const cacheKey = `inv-${providerId}-${productType}-${productId}`;
    const now = Date.now();

    const cached = this.cache.get(cacheKey);
    if (cached && now - cached.fetchedAt < CACHE_TTL_MS) {
      return cached.response;
    }

    const pending = this.pendingRequests.get(cacheKey);
    if (pending) return pending;

    const promise = runWithPerformanceRequestLimit(() =>
      httpService.get<PerformanceResponse>(
        `/v3/products/performance/providers/${providerId}/${productType}/${productId}`
      )
    )
      .then((res) => {
        if (this.pendingRequests.get(cacheKey) === promise) {
          this.cache.set(cacheKey, { response: res.data, fetchedAt: Date.now() });
        }
        return res.data;
      })
      .finally(() => {
        if (this.pendingRequests.get(cacheKey) === promise) {
          this.pendingRequests.delete(cacheKey);
        }
      });

    this.pendingRequests.set(cacheKey, promise);
    return promise;
  }

  async getProviderPerformance(
    productType: PerformanceProductType,
    productId: number
  ): Promise<PerformanceResponse> {
    this.sweepExpiredEntries();

    const cacheKey = `prov-${productType}-${productId}`;
    const now = Date.now();

    const cached = this.cache.get(cacheKey);
    if (cached && now - cached.fetchedAt < CACHE_TTL_MS) {
      return cached.response;
    }

    const pending = this.pendingRequests.get(cacheKey);
    if (pending) return pending;

    const promise = runWithPerformanceRequestLimit(() =>
      httpService.get<PerformanceResponse>(
        `/v3/products/performance/${productType}/${productId}/provider`
      )
    )
      .then((res) => {
        if (this.pendingRequests.get(cacheKey) === promise) {
          this.cache.set(cacheKey, { response: res.data, fetchedAt: Date.now() });
        }
        return res.data;
      })
      .finally(() => {
        if (this.pendingRequests.get(cacheKey) === promise) {
          this.pendingRequests.delete(cacheKey);
        }
      });

    this.pendingRequests.set(cacheKey, promise);
    return promise;
  }

  async getAdminPerformance(
    providerId: number,
    productType: PerformanceProductType,
    productId: number
  ): Promise<PerformanceResponse> {
    this.sweepExpiredEntries();

    const cacheKey = `admin-${providerId}-${productType}-${productId}`;
    const now = Date.now();

    const cached = this.cache.get(cacheKey);
    if (cached && now - cached.fetchedAt < CACHE_TTL_MS) {
      return cached.response;
    }

    const pending = this.pendingRequests.get(cacheKey);
    if (pending) return pending;

    const promise = runWithPerformanceRequestLimit(() =>
      httpService.get<PerformanceResponse>(
        `/v3/products/performance/providers/${providerId}/${productType}/${productId}/admin`
      )
    )
      .then((res) => {
        if (this.pendingRequests.get(cacheKey) === promise) {
          this.cache.set(cacheKey, { response: res.data, fetchedAt: Date.now() });
        }
        return res.data;
      })
      .finally(() => {
        if (this.pendingRequests.get(cacheKey) === promise) {
          this.pendingRequests.delete(cacheKey);
        }
      });

    this.pendingRequests.set(cacheKey, promise);
    return promise;
  }

  async getPerformanceBatch(
    products: InvestorPerformanceBatchRequest[],
    concurrency: number = BATCH_CONCURRENCY
  ): Promise<InvestorPerformanceBatchResult[]> {
    return mapWithConcurrency(products, concurrency, async (product) => {
      try {
        const response = await this.getPerformance(
          product.providerId,
          product.productType,
          product.productId
        );
        return { ...product, response };
      } catch (error) {
        console.error(
          `Failed to load performance for ${product.productType}:${product.productId} (provider ${product.providerId}):`,
          error
        );
        return { ...product, response: null };
      }
    });
  }

  async getProviderPerformanceBatch(
    products: ProviderPerformanceBatchRequest[],
    concurrency: number = BATCH_CONCURRENCY
  ): Promise<ProviderPerformanceBatchResult[]> {
    return mapWithConcurrency(products, concurrency, async (product) => {
      try {
        const response = await this.getProviderPerformance(
          product.productType,
          product.productId
        );
        return { ...product, response };
      } catch (error) {
        console.error(
          `Failed to load performance for ${product.productType}:${product.productId}:`,
          error
        );
        return { ...product, response: null };
      }
    });
  }

  private sweepExpiredEntries(): void {
    const now = Date.now();
    if (now - this.lastSweepAt < CACHE_SWEEP_INTERVAL_MS) return;
    this.lastSweepAt = now;

    for (const [key, entry] of this.cache) {
      if (now - entry.fetchedAt >= CACHE_TTL_MS) {
        this.cache.delete(key);
      }
    }
  }

  _resetForTesting(): void {
    this.cache.clear();
    this.pendingRequests.clear();
    this.lastSweepAt = 0;
  }
}

export const productPerformanceService = new ProductPerformanceService();
